import { usePackagesParams } from '@hooks/use-packages-params';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { Building05, Check } from '@v2/ui/Icon/General';
import { CreditCard02 } from '@v2/ui/Icon/Finance';
import { Plane, Ticket01 } from '@v2/ui/Icon/MapsAndTravel';
import { type SVGComponentProps } from '@v2/ui/Icon/Template';
import { cn } from '@v2/utils/cn';
import { usePathname } from 'next/navigation';
import { Fragment, type FC } from 'react';

const steps = [
  {
    title: 'Flights',
    icon: Plane,
    path: '/search-flights',
  },
  {
    title: 'Tickets',
    icon: Ticket01,
    path: '/search-tickets',
  },
  {
    title: 'Hotels',
    icon: Building05,
    path: '/search-hotel',
  },
  {
    title: 'Checkout',
    icon: CreditCard02,
    path: '/checkout',
  },
] as const;

type StepStatus = 'active' | 'complete' | 'upcoming';

export function BuildPackageSteps() {
  const pathname = usePathname();
  const { packageType } = usePackagesParams();

  const isTicketConfirmed = useCheckoutStepsStore(
    (state) => state.ticketSelected
  );
  const isHotelConfirmed = useCheckoutStepsStore(
    (state) => state.hotelSelected
  );
  const isFlightsConfirmed = useCheckoutStepsStore(
    (state) => state.flightsSelected
  );

  const confirmed = {
    Flights: isFlightsConfirmed,
    Tickets: isTicketConfirmed,
    Hotels: isHotelConfirmed,
    Checkout: false,
  };

  const visibleSteps = steps.filter(
    (step) =>
      packageType !== 'tickets' ||
      step.title === 'Tickets' ||
      step.title === 'Checkout'
  );

  return (
    <div className="flex min-w-0 items-start gap-0 overflow-x-auto min-[360px]:gap-1 min-[600px]:items-center min-[600px]:gap-0 min-[600px]:overflow-visible">
      {visibleSteps.map((step, index) => {
        const status: StepStatus = pathname?.includes(step.path)
          ? 'active'
          : confirmed[step.title]
            ? 'complete'
            : 'upcoming';

        return (
          <Fragment key={step.title}>
            {index > 0 && (
              <div
                aria-hidden
                className="mt-1 flex h-7 w-2 shrink-0 items-center min-[600px]:mt-0 min-[600px]:h-px min-[600px]:w-1.5 lg:w-2"
              >
                <span className="h-px w-full bg-gray-300" />
              </div>
            )}
            <PillStep title={step.title} icon={step.icon} status={status} />
          </Fragment>
        );
      })}
    </div>
  );
}

type PillStepProps = {
  title: string;
  icon: FC<SVGComponentProps>;
  status: StepStatus;
};

function PillStep({ title, icon: Icon, status }: PillStepProps) {
  const isActive = status === 'active';

  return (
    <div
      aria-label={title}
      aria-current={isActive ? 'step' : undefined}
      className="flex flex-col items-center gap-0.5 p-1 min-[600px]:flex-row min-[600px]:gap-0 min-[600px]:p-0"
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full',
          status === 'upcoming' &&
            'size-7 min-[600px]:size-8 min-[600px]:border min-[600px]:border-gray-300 lg:size-10',
          status === 'active' &&
            'size-7 bg-gray-800 min-[600px]:h-8 min-[600px]:w-auto min-[600px]:gap-2 min-[600px]:border min-[600px]:border-gray-800 min-[600px]:px-3 min-[600px]:py-1.5 lg:h-10 lg:gap-2.5 lg:px-4 lg:py-2',
          status === 'complete' &&
            'size-7 bg-transparent text-success-600 min-[600px]:size-8 min-[600px]:border min-[600px]:border-success-600 lg:size-10'
        )}
      >
        {status === 'complete' ? (
          <Check
            size="20"
            pathProps={{ stroke: '#079455' }}
            className="size-4 shrink-0 lg:size-5"
          />
        ) : (
          <Icon
            size="20"
            pathProps={{ stroke: isActive ? 'white' : '#667085' }}
            className="size-4 shrink-0 lg:size-5"
          />
        )}

        {isActive ? (
          <span className="hidden whitespace-nowrap text-xs font-semibold leading-[18px] text-white min-[600px]:inline lg:text-base lg:leading-6">
            {title}
          </span>
        ) : null}
      </div>
      <span
        className={cn(
          'whitespace-nowrap text-xs leading-[18px] min-[600px]:hidden',
          isActive ? 'font-bold text-gray-800' : 'font-medium text-gray-500'
        )}
      >
        {title}
      </span>
    </div>
  );
}
